import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Avatar,
  AvatarFallback,
} from "@b2b/ui-components";
import { Clock, Eye, UserCheck, UserX } from "lucide-react";
import { PendingUser } from "../../types/user.types";

interface RecentUserActivityProps {
  users: PendingUser[];
  isLoading?: boolean;
}

export function RecentUserActivity({
  users,
  isLoading = false,
}: RecentUserActivityProps) {
  const navigate = useNavigate();

  // Get recent users (last 10, sorted by updatedAt)
  const recentUsers = users
    .slice()
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    .slice(0, 10);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent User Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-10 w-10 bg-gray-200 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recentUsers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent User Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No recent user activity</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (user: PendingUser) => {
    if (user.company?.approvalStatus === "pending") {
      return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
    }
    if (user.isActive) {
      return <Badge className="bg-green-100 text-green-700">Active</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>;
  };

  const getUserName = (user: PendingUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  const getUserInitials = (user: PendingUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent User Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recentUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/userprofile/${user.id}`)}
            >
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-orange-100 text-orange-500">
                  {getUserInitials(user)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium truncate">
                    {getUserName(user)}
                  </span>
                  {getStatusBadge(user)}
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <span>{user.email}</span>
                  <span>•</span>
                  <span className="capitalize">
                    {user.role.toLowerCase().replace("_", " ")}
                  </span>
                  {user.company && (
                    <>
                      <span>•</span>
                      <span>{user.company.name}</span>
                    </>
                  )}
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(user.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/userprofile/${user.id}`);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="View Profile"
              >
                <Eye className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t">
          <button
            onClick={() => navigate("/admin/users/list")}
            className="w-full rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
          >
            View All Users →
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
